import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Smartphone, Download, Apple, PlayCircle, CheckCircle, 
  Shield, Zap, Bell, BarChart3, Package, ShoppingCart,
  ArrowLeft, Star, Users, TrendingUp, Lock, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const AppDownload = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const appFeatures = [
    { icon: Bell, title: 'Real-time Notifications', description: 'Get instant alerts for orders, payments, and ads' },
    { icon: BarChart3, title: 'Live Analytics', description: 'Track your business performance on the go' },
    { icon: Package, title: 'Order Management', description: 'Manage orders from anywhere, anytime' },
    { icon: ShoppingCart, title: 'Easy Purchasing', description: 'Browse and purchase plans instantly' },
    { icon: Shield, title: 'Secure Login', description: 'Biometric and secure authentication' },
    { icon: Zap, title: 'Lightning Fast', description: 'Optimized for speed and performance' },
  ];

  const planFeatures = {
    Basic: {
      hasApp: false,
      gradient: 'from-blue-400 to-blue-500',
      description: 'Web dashboard access only',
      upgradeMessage: 'Upgrade to Advanced for mobile app access'
    },
    Advanced: {
      hasApp: true,
      gradient: 'from-blue-500 to-blue-500',
      description: 'Full mobile app access',
      features: ['Android App', 'iOS App', 'Push Notifications', 'Offline Mode']
    },
    Professional: {
      hasApp: true,
      gradient: 'from-orange-500 to-red-500',
      description: 'Premium mobile app + API access',
      features: ['Android App', 'iOS App', 'Push Notifications', 'Offline Mode', 'API Access', 'Priority Updates']
    }
  };

  const currentPlan = user?.activePlan || null;
  const hasAppAccess = currentPlan && planFeatures[currentPlan]?.hasApp;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-slate-800">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-800"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          {user && (
            <Badge className="bg-gradient-to-r from-blue-500 to-blue-500 text-slate-800 border-0">
              {currentPlan ? `${currentPlan} Plan` : 'No Plan'}
            </Badge>
          )}
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-500 mb-6 shadow-lg shadow-blue-500/30">
            <Smartphone className="w-10 h-10 text-slate-800" />
          </div>
          <h1 className="text-4xl md:text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
            DropSquadz Mobile App
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Manage your dropshipping business from anywhere. Download our mobile app for Android and iOS.
          </p>
        </div>

        {/* App Access Status */}
        {user && (
          <Card className={`mb-8 bg-white shadow-lg border ${hasAppAccess ? 'border-green-200' : 'border-yellow-500/30'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  {hasAppAccess ? (
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  ) : (
                    <Lock className="w-12 h-12 text-yellow-400" />
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">
                      {hasAppAccess ? '✅ You have app access!' : '🔒 App Access Locked'}
                    </h3>
                    <p className="text-slate-600">
                      {hasAppAccess 
                        ? `Your ${currentPlan} plan includes mobile app access`
                        : currentPlan 
                          ? 'Upgrade your plan to unlock mobile app'
                          : 'Purchase Advanced or Professional plan for app access'
                      }
                    </p>
                  </div>
                </div>
                {!hasAppAccess && (
                  <Button
                    onClick={() => navigate('/enroll')}
                    className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-pink-600"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Download Buttons */}
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
          {/* Android Download */}
          <Card className={`bg-white shadow-lg border-slate-200 transition-all ${hasAppAccess || !user ? 'hover:border-green-500/50 hover:scale-105 cursor-pointer' : 'opacity-50'}`}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <PlayCircle className="w-8 h-8 text-slate-800" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Download on</p>
                  <h3 className="text-xl font-bold text-slate-800">Google Play</h3>
                </div>
              </div>
              <Button 
                className={`w-full ${hasAppAccess || !user ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600' : 'bg-gray-600 cursor-not-allowed'}`}
                disabled={user && !hasAppAccess}
              >
                <Download className="w-4 h-4 mr-2" />
                {hasAppAccess || !user ? 'Download APK' : 'Upgrade Required'}
              </Button>
              <p className="text-xs text-slate-500 mt-2 text-center">Android 8.0 and above</p>
            </CardContent>
          </Card>

          {/* iOS Download */}
          <Card className={`bg-white shadow-lg border-slate-200 transition-all ${hasAppAccess || !user ? 'hover:border-blue-500/50 hover:scale-105 cursor-pointer' : 'opacity-50'}`}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl flex items-center justify-center border border-gray-600">
                  <Apple className="w-8 h-8 text-slate-800" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Download on</p>
                  <h3 className="text-xl font-bold text-slate-800">App Store</h3>
                </div>
              </div>
              <Button 
                className={`w-full ${hasAppAccess || !user ? 'bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 border border-gray-500' : 'bg-gray-600 cursor-not-allowed'}`}
                disabled={user && !hasAppAccess}
              >
                <Download className="w-4 h-4 mr-2" />
                {hasAppAccess || !user ? 'Coming Soon' : 'Upgrade Required'}
              </Button>
              <p className="text-xs text-slate-500 mt-2 text-center">iOS 14.0 and above</p>
            </CardContent>
          </Card>
        </div>

        {/* App Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center mb-8 text-blue-600">
            App Features
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {appFeatures.map((feature, index) => (
              <Card key={index} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all hover:scale-105 group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <feature.icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Plan Comparison for App Access */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            App Access by Plan
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(planFeatures).map(([planName, plan]) => (
              <Card 
                key={planName} 
                className={`bg-white shadow-lg border-slate-200 transition-all ${currentPlan === planName ? 'ring-2 ring-blue-500' : ''}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className={`bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}>
                      {planName}
                    </CardTitle>
                    {plan.hasApp ? (
                      <Badge className="bg-green-100 text-green-600 border-green-200">
                        <Smartphone className="w-3 h-3 mr-1" />
                        App Access
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-500/20 text-slate-500 border-gray-500/30">
                        Web Only
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-500 text-sm mb-4">{plan.description}</p>
                  {plan.features ? (
                    <ul className="space-y-2">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-yellow-400 text-sm">{plan.upgradeMessage}</p>
                  )}
                  {currentPlan === planName && (
                    <Badge className="mt-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
                      Current Plan
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* App Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: Users, value: '10,000+', label: 'Active Users' },
            { icon: Star, value: '4.8', label: 'App Rating' },
            { icon: Download, value: '50,000+', label: 'Downloads' },
            { icon: TrendingUp, value: '99.9%', label: 'Uptime' },
          ].map((stat, index) => (
            <Card key={index} className="bg-white shadow-lg border-slate-200">
              <CardContent className="p-4 text-center">
                <stat.icon className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold text-blue-600">
                  {stat.value}
                </div>
                <div className="text-xs text-slate-500">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        {!user && (
          <div className="text-center">
            <Card className="bg-gradient-to-r from-blue-500/10 to-blue-500/10 border-blue-500/30 max-w-2xl mx-auto">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-slate-800">Ready to get started?</h3>
                <p className="text-slate-600 mb-6">
                  Sign up now and get access to our mobile app with Advanced or Professional plans.
                </p>
                <div className="flex justify-center gap-4 flex-wrap">
                  <Button
                    onClick={() => navigate('/enroll')}
                    className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    View Plans
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="border-white/30 text-slate-800 hover:bg-slate-100"
                  >
                    Learn More
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppDownload;
